// Copyright (c) Ping. All rights reserved.
//
// Conversation-scoped `ThreadGateway` factory (bug 4). Backs the shared
// `<ThreadOpenPanel>` — both the main chat thread overlay AND the in-call thread
// panel use this so the two are byte-for-byte the same UI + data path. The
// thread identifier is the root message's contentHash; replies are the Dexie
// rows whose envelope `parent_id` equals that hash; `sendReply` posts a
// `com.ping.text` reply (and, for the first reply, a `com.ping.thread_root`
// marker so receivers can anchor the thread), then locally echoes it.

import { liveQuery } from 'dexie';

import {
  encodeEnvelopeJsonBytes,
  lastMessagePreview,
  parseEnvelope,
  persistSentMessage,
  type ReadPointerRecord,
  type ThreadGateway,
  type ThreadIdentifier,
  type ThreadOpenSnapshot,
  type ThreadParentPreview,
  type ThreadReply,
} from '@ping/ui';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { log } from '@core/log/log';
import { db, webReadPointersPersister } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { useSelfProfile } from '@features/profile/selfProfileStore';
import { sendReadPointer } from '@features/readState/deviceGroupReadSync';

import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';

/**
 * Thread read pointers reuse the conversation-scoped `read_pointers` table +
 * DeviceGroup sync, namespaced under `thread:<threadId>` so they never collide
 * with a real conversation's pointer (and the activity feed harmlessly ignores
 * the unmatched key). Zero migration; the existing `com.ping.read_pointer`
 * receive path stores incoming thread pointers by this key automatically.
 */
export const THREAD_READ_POINTER_PREFIX = 'thread:';
export function threadReadPointerKey(threadId: string): string {
  return THREAD_READ_POINTER_PREFIX + threadId;
}

/**
 * Advance a thread's read pointer (monotonic local source of truth) AND
 * broadcast it to the user's OTHER devices over the DeviceGroup so the
 * unread badge is consistent everywhere — exactly like the conversation-
 * level read pointers, just namespaced under `thread:<id>`. Exported so
 * BOTH the shared `createWebThreadGateway` (in-call thread) and the main
 * chat host's inline gateway share one correct implementation (the inline
 * gateway previously had a no-op `markAsRead`, so opening a thread from
 * the main chat never cleared its unread badge).
 */
export async function markThreadReadPointer(threadId: string): Promise<void> {
  const now = Date.now();
  const record: ReadPointerRecord = {
    conversationId: threadReadPointerKey(threadId),
    lastReadAt: now,
    updatedAt: now,
  };
  await webReadPointersPersister.put(record).catch(() => undefined);
  try {
    const status = useSessionStore.getState().status;
    const client = status.kind === 'authenticated' ? status.client : null;
    void sendReadPointer(client, record);
  } catch {
    /* best-effort DeviceGroup mirror — the local write already succeeded */
  }
}

/** Minimal SDK conversation surface the gateway needs to post a reply. */
export interface SendableThreadConv {
  send(plaintext: Uint8Array): Promise<unknown>;
}

export interface WebThreadGatewayOpts {
  /** Host conversation id (hex) the thread lives in. */
  readonly conversationId: string | null;
  /** Display name used for non-self authors in the preview/replies (1:1 peer). */
  readonly peerName: string;
  /**
   * Per-sender identity resolver for GROUP threads — maps a reply's
   * `senderAccountId` to the roster display name + avatar. Without it every
   * non-self reply collapses to the single conversation-level `peerName` with
   * no avatar, so different senders look like the same person. Returns a
   * neutral `name` (never a raw id) when the roster hasn't resolved the sender.
   */
  readonly resolveAuthor?: (
    accountId: string,
  ) => { name: string; avatarUri?: string | undefined };
  /** Resolve the live SDK conversation for sends (host-specific). */
  readonly resolveConv: () => Promise<SendableThreadConv | null>;
}

/**
 * Map a persisted row's `deliveryState` onto a `ThreadReplyStatus` so a
 * thread reply carries its real lifecycle (a failed local echo becomes a
 * retry-able 'failed' tile; an in-flight send shows 'sending'). Peer
 * replies leave `deliveryState` unset → 'sent'.
 */
function replyStatusFromRow(
  deliveryState: 'pending' | 'sent' | 'failed' | undefined,
): ThreadReply['status'] {
  if (deliveryState === 'failed') return 'failed';
  if (deliveryState === 'pending') return 'sending';
  return 'sent';
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i += 1) out += (b[i] ?? 0).toString(16).padStart(2, '0');
  return out;
}

function emptyThreadSnapshot(identifier: ThreadIdentifier): ThreadOpenSnapshot {
  return {
    parent: {
      messageId: identifier as unknown as ThreadParentPreview['messageId'],
      authorId: '' as unknown as ThreadParentPreview['authorId'],
      authorName: '',
      previewText: '',
      timestamp: Date.now(),
      deleted: false,
    },
    replies: [],
    participants: [],
  };
}

/**
 * Build a `ThreadGateway` scoped to one conversation. Stateless apart from the
 * Dexie + SDK ports it closes over, so it's safe to memoize per (conversationId).
 */
export function createWebThreadGateway(opts: WebThreadGatewayOpts): ThreadGateway {
  const { conversationId: convId, peerName, resolveAuthor, resolveConv } = opts;

  // Resolve a reply/parent author → { name, avatarUri }. Prefers the host's
  // group-roster resolver; falls back to the 1:1 peer name (no avatar).
  const authorOf = (
    accountId: string | undefined,
    isSelf: boolean,
  ): { name: string; avatarUri: string | null } => {
    if (isSelf) {
      // Live self avatar (updates instantly on a Settings change) so a self
      // reply / self-rooted call thread shows the real picture, not initials.
      const selfStore = useSelfProfile.getState().avatarUrl;
      return {
        name: 'You',
        avatarUri: typeof selfStore === 'string' && selfStore.length > 0 ? selfStore : null,
      };
    }
    if (resolveAuthor !== undefined && accountId !== undefined && accountId.length > 0) {
      const r = resolveAuthor(accountId);
      return { name: r.name, avatarUri: r.avatarUri ?? null };
    }
    return { name: peerName, avatarUri: null };
  };

  const buildSnapshot = async (identifier: ThreadIdentifier): Promise<ThreadOpenSnapshot> => {
    if (convId === null) return emptyThreadSnapshot(identifier);
    const rows = await db.messages.where('conversationId').equals(convId).toArray();
    // Normal threads root at the root message's content hash. CALL threads root
    // at the shared `call_id` (stable across all devices, unlike a per-device
    // local-echo content hash), so fall back to matching the `call_invite` whose
    // body.call_id equals the identifier — that invite IS the call thread's root.
    const rootRow =
      rows.find((r) => r.contentHash === identifier) ??
      rows.find((r) => {
        if (r.body === undefined || r.body.charAt(0) !== '{') return false;
        const p = parseEnvelope(r.body);
        return (
          p?.kind === 'com.ping.call_invite' &&
          (p.body as { call_id?: unknown }).call_id === identifier
        );
      });
    const identity = await retrieveIdentity();
    const selfId = identity?.accountId ?? '';

    const parentAuthor = authorOf(
      rootRow?.senderAccountId,
      rootRow !== undefined && rootRow.senderAccountId === selfId,
    );
    const parent: ThreadParentPreview = {
      messageId: identifier as unknown as ThreadParentPreview['messageId'],
      authorId: (rootRow?.senderAccountId ?? '') as unknown as ThreadParentPreview['authorId'],
      authorName: parentAuthor.name,
      authorImageUri: parentAuthor.avatarUri,
      previewText: rootRow?.body !== undefined ? lastMessagePreview(rootRow.body) ?? '' : '',
      timestamp: rootRow?.sentAt ?? Date.now(),
      // A redacted/deleted root disables the composer + shows "Original message
      // deleted" (ThreadOpenPanel reads `parent.deleted`). Was hardcoded false →
      // a deleted root stayed repliable with stale preview. Mirrors desktop.
      deleted: rootRow?.redactedAt !== undefined,
    };

    const rootRowId = rootRow?.id;
    const replies: ThreadReply[] = rows
      .filter((r) => {
        if (r.body === undefined) return false;
        const parsed = parseEnvelope(r.body);
        if (parsed === undefined) return false;
        // The thread_root anchor marker is not a reply.
        if (parsed.kind === 'com.ping.thread_root') return false;
        // SENDER path: local-echo body is the FULL envelope → parent_id present.
        if ('parent_id' in parsed && parsed.parent_id === identifier) return true;
        // RECEIVER path: plain-text reply linked via the reconstructed root id.
        return (
          rootRowId !== undefined &&
          rootRowId.length > 0 &&
          r.inReplyTo?.messageId === rootRowId
        );
      })
      .sort((a, b) => a.sentAt - b.sentAt)
      .map((r) => {
        const isSelf = r.senderAccountId === selfId;
        const author = authorOf(r.senderAccountId, isSelf);
        // Project the row's REAL delivery state so a failed local echo
        // surfaces as a retry-able tile (not a phantom "sent"), and a
        // pending send shows the in-flight glyph — parity with the main
        // chat. Peer replies leave `deliveryState` unset → 'sent'.
        const status = replyStatusFromRow(r.deliveryState);
        // Read receipts on this reply (row stores `{accountId, readAt}`;
        // map to the read-side `{userId, timestamp}` shape). Not declared
        // on the core row type — patched on at runtime by the shared
        // `com.ping.read` handler — so read it through a narrow cast.
        const rowReadBy = (r as { readBy?: ReadonlyArray<{ accountId: string; readAt: number }> })
          .readBy;
        const readBy =
          rowReadBy !== undefined && rowReadBy.length > 0
            ? rowReadBy.map((x) => ({ userId: x.accountId, timestamp: x.readAt }))
            : undefined;
        return {
          id: r.id as unknown as ThreadReply['id'],
          threadId: identifier,
          authorId: r.senderAccountId as unknown as ThreadReply['authorId'],
          authorName: author.name,
          authorImageUri: author.avatarUri,
          body: r.body !== undefined ? lastMessagePreview(r.body) ?? '' : '',
          ...(r.body !== undefined ? { rawBody: r.body } : {}),
          timestamp: r.sentAt,
          isSelf,
          status,
          ...(readBy !== undefined ? { readBy } : {}),
          ...(r.editedAt !== undefined ? { editedAt: r.editedAt } : {}),
        };
      });

    return { parent, replies, participants: [] };
  };

  return {
    load: (identifier) => buildSnapshot(identifier),
    subscribe: (identifier, callback) => {
      if (convId === null) return () => undefined;
      const sub = liveQuery(() =>
        db.messages.where('conversationId').equals(convId).count(),
      ).subscribe({
        next: () => {
          void buildSnapshot(identifier).then(callback);
        },
        error: (err: unknown) => {
          log.warn({ event: 'thread.subscribe.failed', err: (err as Error).message });
        },
      });
      return () => sub.unsubscribe();
    },
    sendReply: async (identifier, body) => {
      const trimmed = body.trim();
      if (trimmed.length === 0) return;
      if (convId === null) throw new Error('No conversation.');
      const conv = await resolveConv();
      if (conv === null) throw new Error('Session not ready. Try again in a moment.');

      // First reply? Emit a thread_root marker so peers anchor the thread.
      const existing = await db.messages.where('conversationId').equals(convId).toArray();
      const hasPriorReply = existing.some((r) => {
        if (r.body === undefined) return false;
        const parsed = parseEnvelope(r.body);
        return (
          parsed !== undefined &&
          parsed.kind !== 'com.ping.thread_root' &&
          'parent_id' in parsed &&
          parsed.parent_id === identifier
        );
      });
      const identity = await retrieveIdentity();
      if (!hasPriorReply) {
        await conv.send(
          encodeEnvelopeJsonBytes({
            kind: 'com.ping.thread_root',
            payload: { from: identity?.accountId ?? '' },
            parent_id: identifier,
          }),
        );
      }

      const replyBytes = encodeEnvelopeJsonBytes({
        kind: 'com.ping.text',
        payload: { body: trimmed, thread: true },
        parent_id: identifier,
      });
      const sent = (await conv.send(replyBytes)) as {
        content_hash: Uint8Array;
        epoch: number | bigint;
      };

      if (identity) {
        const sentAt = Date.now();
        await persistSentMessage({
          messages: webMessagesPersister,
          conversations: webConversationsPersister,
          record: {
            id:
              typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `local-reply-${sentAt}-${Math.random().toString(36).slice(2, 8)}`,
            conversationId: convId,
            epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
            serverSeq: sentAt,
            contentHash: bytesToHex(sent.content_hash),
            senderAccountId: identity.accountId,
            body: JSON.stringify({
              kind: 'com.ping.text',
              payload: { body: trimmed, thread: true },
              parent_id: identifier,
            }),
            sentAt,
            deliveryState: 'sent',
          },
        });
      }
    },
    // Advance + broadcast the thread's read pointer (see `markThreadReadPointer`).
    markAsRead: async (identifier) => {
      await markThreadReadPointer(String(identifier));
    },
  };
}
